/**
 * L'écran « Messages pré-session » : lecture de l'état et libellés.
 *
 * L'état vient de `public.etat_envois_pre_session` (migration
 * `20261012160000_les_informations_avant_le_stage.sql`). Le déroulé : un clic
 * lance la première vague d'une semaine ; ensuite, chaque matin à 8 h, un
 * rattrapage sert les inscrits tardifs des sessions lancées, jusqu'au premier
 * jour. Une session qui atteint le seuil après le clic attend une approbation.
 *
 * Module pur : aucun appel réseau, testable tel quel.
 */

export type ChoixSession = 'forcer' | 'suspendre' | null;

export interface ReglagesPreSession {
  envois_suspendus: boolean;
  seuil: number;
  jours_avant: number;
  heure_rattrapage: string;
  dernier_rattrapage_le: string | null;
  heure_debut_envois: string;
  heure_fin_envois: string;
  lot_max: number;
  plafond_quotidien: number;
  pause_jusqu_a: string | null;
  pause_motif: string | null;
}

export interface SessionPreSession {
  session_id: string;
  start_date: string;
  end_date: string;
  periode: string | null;
  semaine: string | null;
  center_name: string | null;
  stage_title: string | null;
  choix: ChoixSession;
  enfants_attendus: number;
  familles_attendues: number;
  seuil_atteint: boolean;
  /** Au seuil ou forcée, et pas suspendue. */
  eligible: boolean;
  lancee_le: string | null;
  lancee_origine: 'vague' | 'approbation' | null;
  envoyes: number;
  en_attente: number;
  echecs: number;
  familles_sans_info: number;
  derniere_erreur: string | null;
}

export interface EtatPreSession {
  reglages: ReglagesPreSession;
  envoyes_24h: number;
  sessions: SessionPreSession[];
}

export interface Lancement {
  sessions_lancees: number;
  sessions: number;
  deja_servis: number;
  planifies: number;
  expediteur_reveille: boolean;
}

/** `09:00:00` → `9 h`, `08:30:00` → `8 h 30`. */
export function heure(h: string): string {
  const [hh, mm] = h.split(':');
  const heures = String(Number(hh));
  return mm && mm !== '00' ? `${heures} h ${mm}` : `${heures} h`;
}

const JOURS = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'];

/** `2026-10-19` → `lun. 19/10`, sans dépendre du fuseau du navigateur. */
export function jourCourt(date: string): string {
  const [a, m, j] = date.slice(0, 10).split('-').map(Number);
  const d = new Date(Date.UTC(a, m - 1, j));
  return `${JOURS[d.getUTCDay()]} ${String(j).padStart(2, '0')}/${String(m).padStart(2, '0')}`;
}

/** Jours entre deux dates `AAAA-MM-JJ`, comptés en jours civils. */
export function joursEntre(du: string, au: string): number {
  const t = (s: string) => {
    const [a, m, j] = s.slice(0, 10).split('-').map(Number);
    return Date.UTC(a, m - 1, j);
  };
  return Math.round((t(au) - t(du)) / 86_400_000);
}

export function libelleJours(jours: number): string {
  if (jours === 0) return "Aujourd'hui";
  if (jours === 1) return 'Demain';
  if (jours < 0) return `Commencé il y a ${-jours} jour${jours < -1 ? 's' : ''}`;
  return `Dans ${jours} jours`;
}

function familles(n: number): string {
  return `${n} famille${n > 1 ? 's' : ''}`;
}

/* ------------------------------------------------------------------------ */
/* Les semaines                                                             */
/* ------------------------------------------------------------------------ */

export interface SemainePreSession {
  /** Le jour de début commun, `AAAA-MM-JJ`. */
  debut: string;
  sessions: SessionPreSession[];
  /** Au moins une session lancée : la première vague est partie. */
  lancee: boolean;
  /** Éligibles et pas encore lancées. */
  aLancer: SessionPreSession[];
  /** Familles que le clic servirait. */
  famillesALancer: number;
  sousLeSeuil: SessionPreSession[];
}

/** Les sessions regroupées par jour de début, dans l'ordre. */
export function parSemaine(sessions: SessionPreSession[]): SemainePreSession[] {
  const groupes = new Map<string, SessionPreSession[]>();
  for (const s of sessions) {
    const cle = s.start_date.slice(0, 10);
    groupes.set(cle, [...(groupes.get(cle) ?? []), s]);
  }
  return [...groupes.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([debut, liste]) => {
      const aLancer = liste.filter((s) => s.eligible && !s.lancee_le);
      return {
        debut,
        sessions: liste,
        lancee: liste.some((s) => s.lancee_le),
        aLancer,
        famillesALancer: aLancer.reduce((n, s) => n + s.familles_sans_info, 0),
        sousLeSeuil: liste.filter((s) => s.choix !== 'suspendre' && !s.eligible),
      };
    });
}

/**
 * L'alerte d'une semaine dont la première vague n'est pas lancée, à partir de
 * `jours_avant` jours du début. Aucun lancement automatique : seulement l'alerte.
 */
export function alerteSemaine(s: SemainePreSession, aujourdHui: string, r: ReglagesPreSession): string | null {
  const jours = joursEntre(aujourdHui, s.debut);
  if (s.lancee || s.aLancer.length === 0 || jours < 0 || jours > r.jours_avant) return null;
  return `Semaine du ${jourCourt(s.debut)} : première vague non lancée, J-${jours}`;
}

/* ------------------------------------------------------------------------ */
/* Les sessions                                                             */
/* ------------------------------------------------------------------------ */

export type Situation =
  | 'suspendue'
  | 'sous_le_seuil'
  | 'a_lancer'
  | 'a_approuver'
  | 'en_cours'
  | 'servie'
  | 'au_rattrapage';

/**
 * La situation d'une session. `semaineLancee` distingue « à lancer » (la vague
 * n'est pas partie) de « à approuver » (le seuil a été atteint après le clic).
 */
export function situation(s: SessionPreSession, semaineLancee: boolean): Situation {
  if (s.choix === 'suspendre') return 'suspendue';
  if (!s.lancee_le) {
    if (!s.eligible) return 'sous_le_seuil';
    return semaineLancee ? 'a_approuver' : 'a_lancer';
  }
  if (s.en_attente > 0) return 'en_cours';
  if (s.familles_sans_info === 0) return 'servie';
  return 'au_rattrapage';
}

export function libelleSituation(s: SessionPreSession, semaineLancee: boolean, r: ReglagesPreSession): string {
  switch (situation(s, semaineLancee)) {
    case 'suspendue':
      return 'Envoi suspendu';
    case 'sous_le_seuil':
      return `Sous le seuil de ${r.seuil} : ${familles(s.familles_sans_info)} sans information`;
    case 'a_lancer':
      return `À lancer : ${familles(s.familles_sans_info)}`;
    case 'a_approuver':
      return `Seuil atteint après la vague : à approuver (${familles(s.familles_sans_info)})`;
    case 'en_cours':
      return `Envoi en cours : ${s.en_attente} en attente`;
    case 'servie':
      return 'Toutes les familles sont servies';
    case 'au_rattrapage':
      return `${familles(s.familles_sans_info)} à servir au rattrapage de ${heure(r.heure_rattrapage)}`;
  }
}

/** Le compte rendu d'un clic. */
export function resumeLancement(p: Lancement, r: ReglagesPreSession): string {
  if (p.planifies === 0 && p.sessions_lancees === 0) {
    return 'Rien de nouveau : les sessions éligibles sont déjà lancées et leurs familles servies ou planifiées.';
  }
  const rythme = `jusqu'à ${r.lot_max} courriels par minute, de ${heure(r.heure_debut_envois)} à ${heure(r.heure_fin_envois)}`;
  const reveil = p.expediteur_reveille ? '' : " L'expéditeur n'a pas pu être réveillé : les envois partiront au prochain passage du calendrier.";
  return `${p.sessions_lancees} session(s) lancée(s), ${p.planifies} envoi(s) planifié(s). Ils partent au rythme de l'expéditeur (${rythme}) ; ensuite, rattrapage chaque matin à ${heure(r.heure_rattrapage)} jusqu'au premier jour.${reveil}`;
}

/** Une erreur de l'API, dite en clair. */
export function erreurEnClair(err: unknown): string {
  const o = (err ?? {}) as { code?: string; message?: string };
  if (o.code === 'PGRST202' || o.code === 'PGRST205') {
    return "L'envoi pré-session n'est pas encore installé sur cette base (migration 20261012160000_les_informations_avant_le_stage). L'ancien envoi reste possible depuis l'application actuelle.";
  }
  if (o.code === '42501') return "Vous n'avez pas les droits pour cette action.";
  // Les refus de la base portent une étiquette entre crochets : on la retire.
  return (o.message ?? '').replace(/^\[[a-z-]+\]\s*/, '') || 'Erreur inconnue';
}
